import { useReducer } from 'react';
import { TDocumentFeedbackItem, TDocuments } from 'lendingApi/lendingApi.types';
import { TFileStatusGroup, TUploadRejectReasonCode } from 'types';
import { TFileStatus } from '../../UploadedFiles/UploadedFiles';
import { translateUploadRejectReason } from '../helpers/rejectReasonHelper';

enum ACTIONS {
  SET_ALL_FILES = 'SET_ALL_FILES',
  SET_FILE_LOADING = 'SET_FILE_LOADING',
  SET_FILE_SUCCESS = 'SET_FILE_SUCCESS',
  SET_FILE_ERROR = 'SET_FILE_ERROR',
  SET_FILE_REJECT = 'SET_FILE_REJECT',
  REMOVE_FILE = 'REMOVE_FILE',
}

export type TFile = {
  id: string;
  fileName: string;
  status: TFileStatus;
  statusGroup: TFileStatusGroup;
  errorReason?: string;
  rejectReason?: string;
  docsFeedback?: TDocumentFeedbackItem[];
};

type TSetAllFilesAction = {
  type: typeof ACTIONS.SET_ALL_FILES;
  payload: {
    includeFeedback?: boolean;
    files: {
      id: string;
      attributes: {
        document_name: string;
        status: string;
        reject_reason?: TUploadRejectReasonCode;
        document_feedback?: TDocumentFeedbackItem[];
      };
    }[];
  };
};

type TSetRemoveAction = {
  type: ACTIONS.REMOVE_FILE;
  payload: {
    id: string;
  };
};

type TSetLoadingAction = {
  type: ACTIONS.SET_FILE_LOADING;
  payload: {
    id: string;
    fileName: string;
  };
};

type TSetSuccessAction = {
  type: ACTIONS.SET_FILE_SUCCESS;
  payload: {
    id: string;
    status: TFileStatus;
  };
};

type TSetErrorAction = {
  type: ACTIONS.SET_FILE_ERROR;
  payload: {
    id: string;
    errorReason: string;
  };
};

type TSetRejectAction = {
  type: ACTIONS.SET_FILE_REJECT;
  payload: {
    id: string;
    rejectReason: TUploadRejectReasonCode;
  };
};

type TReducerAction =
  | TSetAllFilesAction
  | TSetRemoveAction
  | TSetLoadingAction
  | TSetSuccessAction
  | TSetErrorAction
  | TSetRejectAction;

export function useFileUploadReducer() {
  const reducer = (state: TFile[], action: TReducerAction) => {
    switch (action.type) {
      case ACTIONS.SET_ALL_FILES: {
        const { files, includeFeedback } = action.payload;
        return (
          files?.map((file) => {
            const { id } = file;
            const { document_name, status, reject_reason, document_feedback } =
              file.attributes;

            if (includeFeedback && document_feedback?.length) {
              const allDone = document_feedback.every(
                (feedback) => feedback.marked_as_done_by_customer,
              );
              const result: TFile = {
                id,
                fileName: document_name,
                status: allDone ? 'overridden' : 'rejected',
                statusGroup: allDone ? 'overridden' : 'rejected',
                docsFeedback: document_feedback,
              };
              return result;
            }

            if (status === 'rejected') {
              const result: TFile = {
                id,
                fileName: document_name,
                status: 'rejected',
                statusGroup: 'rejected',
                rejectReason: reject_reason
                  ? translateUploadRejectReason(reject_reason)
                  : 'Document cannot be validated. Please upload another document.',
              };
              return result;
            }

            const result: TFile = {
              id,
              fileName: document_name,
              status: 'success',
              statusGroup: status === 'complete' ? 'approved' : 'uploaded',
            };
            return result;
          }) ?? []
        );
      }
      case ACTIONS.SET_FILE_LOADING:
        return [
          ...state,
          {
            id: action.payload.id,
            fileName: action.payload.fileName,
            status: 'loading' as TFileStatus,
            statusGroup: 'uploaded' as TFileStatusGroup,
          },
        ];
      case ACTIONS.SET_FILE_ERROR: {
        const fileToUpdateIndex = state.findIndex(
          (file) => file.id === action.payload.id,
        );
        if (fileToUpdateIndex === -1) return state;
        const updatedFile = {
          ...state[fileToUpdateIndex],
          status: 'error' as TFileStatus,
          errorReason: action.payload.errorReason,
        };

        return state
          .slice(0, fileToUpdateIndex)
          .concat(updatedFile)
          .concat(state.slice(fileToUpdateIndex + 1));
      }
      case ACTIONS.SET_FILE_REJECT: {
        const fileToUpdateIndex = state.findIndex(
          (file) => file.id === action.payload.id,
        );
        if (fileToUpdateIndex === -1) return state;

        const rejectReason = action.payload.rejectReason
          ? translateUploadRejectReason(action.payload.rejectReason)
          : 'Document cannot be validated. Please upload another document.';

        const updatedFile = {
          ...state[fileToUpdateIndex],
          status: 'rejected' as TFileStatus,
          rejectReason: rejectReason,
          statusGroup: 'rejected' as TFileStatusGroup,
        };

        return state
          .slice(0, fileToUpdateIndex)
          .concat(updatedFile)
          .concat(state.slice(fileToUpdateIndex + 1));
      }
      case ACTIONS.SET_FILE_SUCCESS: {
        const fileToUpdateIndex = state.findIndex(
          (file) => file.id === action.payload.id,
        );
        if (fileToUpdateIndex === -1) return state;
        const updatedFile = {
          ...state[fileToUpdateIndex],
          status: action.payload.status,
        };

        return state
          .slice(0, fileToUpdateIndex)
          .concat(updatedFile)
          .concat(state.slice(fileToUpdateIndex + 1));
      }
      case ACTIONS.REMOVE_FILE:
        return state.filter((file) => file.id !== action.payload.id);
      default:
        return state;
    }
  };

  const [uploadedFiles, dispatch] = useReducer(reducer, []);

  const setAllFiles = (files: TDocuments[], includeFeedback?: boolean) => {
    dispatch({
      type: ACTIONS.SET_ALL_FILES,
      payload: {
        files,
        includeFeedback,
      },
    });
  };

  const setFileLoading = (id: string, fileName: string) => {
    dispatch({
      type: ACTIONS.SET_FILE_LOADING,
      payload: {
        id,
        fileName,
      },
    });
  };

  const setFileSuccess = (id: string) => {
    dispatch({
      type: ACTIONS.SET_FILE_SUCCESS,
      payload: {
        id,
        status: 'success',
      },
    });
  };

  const setFileError = (id: string, errorReason: string) => {
    dispatch({
      type: ACTIONS.SET_FILE_ERROR,
      payload: {
        id,
        errorReason,
      },
    });
  };

  const setFileReject = (id: string, rejectReason: TUploadRejectReasonCode) => {
    dispatch({
      type: ACTIONS.SET_FILE_REJECT,
      payload: {
        id,
        rejectReason,
      },
    });
  };

  const removeFile = (id: string) => {
    dispatch({
      type: ACTIONS.REMOVE_FILE,
      payload: {
        id,
      },
    });
  };

  return {
    uploadedFiles,
    setAllFiles,
    setFileLoading,
    setFileSuccess,
    setFileError,
    setFileReject,
    removeFile,
  };
}
